import { SWOTAnalysisResult } from '../types';
import { encryptString, decryptString } from './security';

// تشفير المفتاح قبل تخزينه في المتصفح
const API_KEY = 'sk-772aedf35e6648f09e8675ce46902c2e';
const encryptedKey = encryptString(API_KEY);
const getApiKey = () => decryptString(encryptedKey);

export async function generateAnalysis(description: string): Promise<SWOTAnalysisResult> {
  try {
    // تشفير وصف المشروع
    const encryptedDescription = encryptString(description);
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    try {
      const response = await fetch('https://api.deepseek.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getApiKey()}`
        },
        signal: controller.signal,
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [{
            role: 'user',
            content: `
              قم بتحليل المشروع التالي وتقديم تحليل SWOT مفصل باللغة العربية.
              
              وصف المشروع: ${decryptString(encryptedDescription)}
              
              قم بتنسيق الرد كـ JSON بالهيكل التالي:
              {
                "strengths": ["نقطة قوة 1", "نقطة قوة 2", ...],
                "weaknesses": ["نقطة ضعف 1", "نقطة ضعف 2", ...],
                "opportunities": ["فرصة 1", "فرصة 2", ...],
                "threats": ["تهديد 1", "تهديد 2", ...]
              }
              
              يجب أن تكون جميع الردود باللغة العربية وتوفير 3-4 نقاط على الأقل لكل فئة.
              اجعل التحليل عميقاً ومفصلاً ومناسباً للسوق السعودي.
            `
          }],
          temperature: 0.7,
          max_tokens: 2000,
          response_format: { type: "json_object" }
        })
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error?.message || 'فشل في الاتصال بخدمة الذكاء الاصطناعي');
      }

      const data = await response.json();
      
      if (!data?.choices?.[0]?.message?.content) {
        throw new Error('استجابة غير صالحة من خدمة الذكاء الاصطناعي');
      }

      // تشفير النتيجة قبل معالجتها
      const encryptedContent = encryptString(data.choices[0].message.content);
      const analysis = JSON.parse(decryptString(encryptedContent));
      
      if (!validateAnalysis(analysis)) {
        throw new Error('البيانات المستلمة غير مكتملة أو غير صالحة');
      }

      // تشفير التحليل النهائي
      return {
        strengths: analysis.strengths.map(encryptString),
        weaknesses: analysis.weaknesses.map(encryptString),
        opportunities: analysis.opportunities.map(encryptString),
        threats: analysis.threats.map(encryptString)
      };
    } finally {
      clearTimeout(timeoutId);
    }
  } catch (error) {
    console.error('Error in generateAnalysis:', error);
    if (error.name === 'AbortError') {
      throw new Error('انتهت مهلة الاتصال. الرجاء المحاولة مرة أخرى.');
    }
    throw error instanceof Error ? error : new Error('حدث خطأ غير متوقع');
  }
}

function validateAnalysis(analysis: any): analysis is SWOTAnalysisResult {
  if (!analysis || typeof analysis !== 'object') return false;

  const requiredArrays = ['strengths', 'weaknesses', 'opportunities', 'threats'];
  return requiredArrays.every(key => 
    Array.isArray(analysis[key]) && 
    analysis[key].length >= 3 && 
    analysis[key].every(item => typeof item === 'string' && item.trim().length > 0)
  );
}